import React from "react";
import {
  FaArchive,
  FaCompressAlt,
  FaEyeSlash,
  FaExpandAlt,
  FaSearch,
  FaSortAmountDown,
  FaTimes,
} from "react-icons/fa";

import {
  TASK_GROUP_BY_OPTIONS,
  TASK_PRIORITY_OPTIONS,
  TASK_SORT_OPTIONS,
  TASK_STATUS_OPTIONS,
} from "../data/tasksKanbanData";

export default function TaskKanbanToolbar({
  filters,
  updateFilter,
  resetFilters,
  productsApps,
  totalColumns = 0,
  visibleColumns = 0,
  allCardsExpanded,
  onToggleAllCards,
}) {
  const hasSearch = String(filters.q || "").trim().length > 0;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative w-full max-w-md sm:w-[360px]">
        <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/55" />
        <input
          type="text"
          value={filters.q}
          onChange={(event) => updateFilter("q", event.target.value)}
          placeholder="Rechercher tâche, client, app, note..."
          className="h-10 w-full rounded-xl border border-white/15 bg-white/10 py-2 pl-9 pr-3 text-sm font-medium text-white outline-none transition placeholder:text-white/50 hover:bg-white/15 focus:border-sky-300 focus:bg-white/15 focus:ring-4 focus:ring-sky-300/10"
        />
      </div>

      <select
        value={filters.group_by}
        onChange={(event) => updateFilter("group_by", event.target.value)}
        className="h-10 min-w-[215px] rounded-xl border border-white/15 bg-white/10 px-3 text-sm font-bold text-white outline-none transition hover:bg-white/15 focus:border-sky-300"
      >
        {TASK_GROUP_BY_OPTIONS.map((option) => (
          <option className="text-slate-900" key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <select
        value={filters.status}
        onChange={(event) => updateFilter("status", event.target.value)}
        className="h-10 rounded-xl border border-white/15 bg-white/10 px-3 text-sm font-bold text-white outline-none transition hover:bg-white/15 focus:border-sky-300"
      >
        <option className="text-slate-900" value="all">
          Tous les statuts
        </option>

        {TASK_STATUS_OPTIONS.map((option) => (
          <option
            className="text-slate-900"
            key={`${option.value}-${option.label}`}
            value={option.value}
          >
            {option.label}
          </option>
        ))}
      </select>

      <select
        value={filters.priority}
        onChange={(event) => updateFilter("priority", event.target.value)}
        className="h-10 rounded-xl border border-white/15 bg-white/10 px-3 text-sm font-bold text-white outline-none transition hover:bg-white/15 focus:border-sky-300"
      >
        <option className="text-slate-900" value="all">
          Toutes les priorités
        </option>

        {TASK_PRIORITY_OPTIONS.map((option) => (
          <option className="text-slate-900" key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <select
        value={filters.product_app_id}
        onChange={(event) => updateFilter("product_app_id", event.target.value)}
        className="h-10 min-w-[210px] rounded-xl border border-white/15 bg-white/10 px-3 text-sm font-bold text-white outline-none transition hover:bg-white/15 focus:border-sky-300"
      >
        <option className="text-slate-900" value="all">
          Toutes les applications
        </option>
        <option className="text-slate-900" value="null">
          Sans application
        </option>

        {[...(productsApps || [])]
          .sort((a, b) => String(a.nom_app).localeCompare(String(b.nom_app), "fr"))
          .map((app) => (
            <option className="text-slate-900" key={app.id} value={app.id}>
              {app.nom_app}
            </option>
          ))}
      </select>

      <select
        value={filters.sort_due}
        onChange={(event) => updateFilter("sort_due", event.target.value)}
        className="h-10 min-w-[215px] rounded-xl border border-white/15 bg-white/10 px-3 text-sm font-bold text-white outline-none transition hover:bg-white/15 focus:border-sky-300"
      >
        {TASK_SORT_OPTIONS.map((option) => (
          <option className="text-slate-900" key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <button
        type="button"
        onClick={() =>
          updateFilter("hideEmptyColumns", !filters.hideEmptyColumns)
        }
        className={`inline-flex h-10 items-center gap-2 rounded-xl px-3 text-sm font-bold text-white transition ${
          filters.hideEmptyColumns || hasSearch
            ? "bg-sky-400/25 ring-1 ring-sky-200/25"
            : "bg-white/10 hover:bg-white/15"
        }`}
      >
        <FaEyeSlash />
        {filters.hideEmptyColumns || hasSearch
          ? `Colonnes ${visibleColumns}/${totalColumns}`
          : "Masquer vides"}
      </button>

      <button
        type="button"
        onClick={onToggleAllCards}
        className={`inline-flex h-10 items-center gap-2 rounded-xl px-3 text-sm font-bold text-white transition ${
          allCardsExpanded
            ? "bg-emerald-400/25 ring-1 ring-emerald-200/25"
            : "bg-white/10 hover:bg-white/15"
        }`}
      >
        {allCardsExpanded ? <FaCompressAlt /> : <FaExpandAlt />}
        {allCardsExpanded ? "Tout diminuer" : "Tout agrandir"}
      </button>

      <button
        type="button"
        onClick={() =>
          updateFilter(
            "archived",
            filters.archived === "true" ? "false" : "true",
          )
        }
        className={`inline-flex h-10 items-center gap-2 rounded-xl px-3 text-sm font-bold text-white transition ${
          filters.archived === "true"
            ? "bg-amber-400/25 ring-1 ring-amber-200/25"
            : "bg-white/10 hover:bg-white/15"
        }`}
      >
        <FaArchive />
        {filters.archived === "true" ? "Actives" : "Archives"}
      </button>

      <button
        type="button"
        onClick={resetFilters}
        className="inline-flex h-10 items-center gap-2 rounded-xl bg-white/10 px-3 text-sm font-bold text-white transition hover:bg-white/15"
      >
        <FaTimes />
        Reset
      </button>
    </div>
  );
}